import {KeyboardEvent, ReactNode, useEffect, useRef, useState} from "react";

export type OtsInlineEditProps = {
  /** The current value, as text. */
  value: string;
  /**
   * Saves a changed value. May return a promise: the field stays open and disabled until it settles,
   * and shows the rejection's message if it fails.
   */
  onSave: (value: string) => void | Promise<unknown>;
  /** What is being edited, e.g. "Pickup quantity"; names the control for assistive technology. */
  label: string;
  type?: "text" | "number";
  /** How the value reads when not being edited; defaults to the value itself. */
  children?: ReactNode;
  align?: "start" | "end";
  disabled?: boolean;
  /** Returns why a value cannot be saved, or nothing when it can. */
  validate?: (value: string) => string | undefined;
  className?: string;
};

/**
 * A value that becomes a field when clicked: Enter or leaving the field saves, Escape cancels.
 * For correcting a cell in place, without opening a form.
 */
export const OtsInlineEdit = ({
  value,
  onSave,
  label,
  type = "text",
  children,
  align = "start",
  disabled = false,
  validate,
  className,
}: OtsInlineEditProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const open = () => {
    setDraft(value);
    setError(undefined);
    setEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    if (next === value.trim()) return setEditing(false);
    const problem = validate?.(next);
    if (problem) return setError(problem);
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // The grid around it has its own arrow-key and Escape handling.
    event.stopPropagation();
    if (event.key === "Enter") void commit();
    else if (event.key === "Escape") setEditing(false);
  };

  if (editing) {
    return (
      <span className={["relative inline-flex max-w-full", className].filter(Boolean).join(" ")} onClick={(event) => event.stopPropagation()}>
        <input
          ref={input}
          type={type}
          inputMode={type === "number" ? "decimal" : undefined}
          step={type === "number" ? "any" : undefined}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          value={draft}
          disabled={saving}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(undefined);
          }}
          onBlur={() => void commit()}
          onKeyDown={onKeyDown}
          className={[
            "min-w-0 rounded border bg-[var(--ots-field)] px-1 py-0 text-inherit outline-none focus:ring-2",
            type === "number" ? "w-20 tabular-nums" : "w-28",
            align === "end" ? "text-right" : "",
            error ? "border-[var(--ots-error)] focus:ring-[var(--ots-error)]/30" : "border-[var(--ots-primary)] focus:ring-[var(--ots-primary)]/30",
            saving ? "opacity-60" : "",
          ].join(" ")}/>
        {error && (
          <span role="alert" className={["absolute top-full z-30 mt-1 whitespace-nowrap rounded bg-[var(--ots-error)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--ots-on-error)] shadow", align === "end" ? "right-0" : "left-0"].join(" ")}>
            {error}
          </span>
        )}
      </span>
    );
  }

  if (disabled) return <span className={className}>{children ?? value}</span>;

  return (
    <button
      type="button"
      aria-label={`Edit ${label}: ${value || "empty"}`}
      onClick={(event) => {
        // The row behind it is clickable too; editing a cell should not also open the row.
        event.stopPropagation();
        open();
      }}
      title="Click to edit"
      // Takes exactly the room of the value it wraps, so a cell reads the same whether or not it is editable.
      className={[
        "-mx-1 inline-flex max-w-[calc(100%+0.5rem)] cursor-text items-center rounded px-1 text-inherit underline decoration-dotted decoration-[var(--ots-outline)] underline-offset-4 outline-none hover:bg-[var(--ots-primary-container)] hover:decoration-[var(--ots-primary)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]",
        type === "number" ? "whitespace-nowrap" : "min-w-0",
        className,
      ].filter(Boolean).join(" ")}>
      {type === "number" ? children ?? value : <span className="min-w-0 truncate">{children ?? value}</span>}
    </button>
  );
};
